import { faCheck, faClock, faRotateLeft, faUserGroup } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormEvent, ReactNode, useEffect, useState } from 'react'
import { NavLink, useSearchParams } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import { Page } from '~/components/Page'
import { OutlineButton, Panel, PRIZE_GREEN, Skeleton, StateBlock } from '~/components/ui'
import { SectionHeading, SectionSubheading } from '~/routes/landing/SectionHeading'
import {
    ApplicationDraft,
    useAffiliateApplication,
    useSubmitAffiliateApplication,
} from '../api/useAffiliateApplication'

const EMPTY: ApplicationDraft = { audience: '', audience_size: '', pitch: '', kind: 'creator' }

const Field = ({
    label,
    hint,
    htmlFor,
    children,
}: {
    label: string
    hint?: string
    htmlFor: string
    children: ReactNode
}) => (
    <div className='flex flex-col gap-2'>
        <label htmlFor={htmlFor} className='text-2xs font-bold uppercase tracking-[0.2em] text-white/50'>
            {label}
        </label>
        {hint && <p className='-mt-1 text-xs font-medium leading-relaxed text-white/40'>{hint}</p>}
        {children}
    </div>
)

const inputClass = twMerge(
    'w-full border border-white/15 bg-black/40 px-3 py-2.5 text-sm',
    'transition-colors placeholder:text-white/25 focus:border-white/50 focus:outline-none'
)

/** Applying, and the three things that can already be true when you arrive. */
export const AffiliateApplyPage = () => {
    const { data: applications, isLoading } = useAffiliateApplication()
    const submit = useSubmitAffiliateApplication()
    const [params] = useSearchParams()
    // `?as=agency` comes from the second button on the landing page. The type is
    // carried in the URL rather than as a control on this form: the choice was
    // already made on the page before, and asking again invites a mismatch
    // between the button someone pressed and the box they leave ticked.
    const applyingAsAgency = params.get('as') === 'agency'
    const [draft, setDraft] = useState<ApplicationDraft>({
        ...EMPTY,
        kind: applyingAsAgency ? 'manager' : 'creator',
    })

    // The slot for the role this page is about. The other one is reported at the
    // bottom so someone approved for one can find the other.
    const role = applyingAsAgency ? 'manager' : 'creator'
    const application = applications?.[role]
    const otherRole = applyingAsAgency ? 'creator' : 'manager'
    const other = applications?.[otherRole]

    // Prefill from a previous attempt so a rejected applicant edits their pitch
    // rather than retyping it from nothing.
    useEffect(() => {
        if (!application || application.status === null) return
        setDraft({
            audience: application.audience ?? '',
            audience_size: application.audience_size ?? '',
            pitch: application.pitch ?? '',
            kind: role,
        })
    }, [application?.status, role])

    // A pointer to whichever role they have not taken up yet. Two roles applied
    // for separately need somewhere that says the second one exists.
    const otherRolePrompt =
        other && other.status === null ? (
            <p className='text-center text-sm font-medium text-white/45'>
                {applyingAsAgency ? (
                    <>
                        Also want a referral link for players?{' '}
                        <NavLink to='/affiliate/apply' className='underline hover:text-white'>
                            Apply as a creator
                        </NavLink>{' '}
                        as well — they are separate roles and you can hold both.
                    </>
                ) : (
                    <>
                        Manage a roster of streamers?{' '}
                        <NavLink to='/affiliate/apply?as=agency' className='underline hover:text-white'>
                            Apply as an agency
                        </NavLink>{' '}
                        as well — they are separate roles and you can hold both.
                    </>
                )}
            </p>
        ) : null

    const [reapplying, setReapplying] = useState(false)

    const onSubmit = (event: FormEvent) => {
        event.preventDefault()
        submit.mutate(draft, { onSuccess: () => setReapplying(false) })
    }

    const shell = (children: ReactNode) => (
        <Page title='Affiliate Application' header='Affiliate'>
            <div className='mx-auto flex w-full max-w-[720px] flex-col gap-8 px-4 py-10 md:px-8 md:py-16'>
                {children}
            </div>
        </Page>
    )

    if (isLoading) {
        return shell(
            <div className='flex flex-col gap-3'>
                <Skeleton className='h-10 w-2/3' />
                <Skeleton className='h-56 w-full' />
            </div>
        )
    }

    const status = application?.status ?? null

    if (status === 'approved') {
        return shell(
            <>
            <StateBlock
                icon={faCheck}
                title={applyingAsAgency ? "You're an agency" : "You're in"}
                action={
                    <NavLink to={applyingAsAgency ? '/affiliate/manager-tools' : '/affiliate/tools'}>
                        <OutlineButton>
                            {applyingAsAgency ? 'Open manager tools' : 'Open affiliate tools'}
                        </OutlineButton>
                    </NavLink>
                }
            >
                {applyingAsAgency
                    ? 'Your recruiting link and roster dashboard are ready.'
                    : 'Your referral link and dashboard are ready.'}
            </StateBlock>
            {otherRolePrompt}
            </>
        )
    }

    if (status === 'pending' && !reapplying) {
        return shell(
            <>
            <StateBlock
                icon={faClock}
                title='Application received'
                action={<OutlineButton onClick={() => setReapplying(true)}>Edit my answers</OutlineButton>}
            >
                A person reads every one of these, usually within a few days. You will hear back either way — no
                need to send it again.
            </StateBlock>
            {otherRolePrompt}
            </>
        )
    }

    return shell(
        <>
            <div className='flex flex-col gap-3'>
                <SectionHeading>{draft.kind === 'manager' ? 'Apply as an agency' : 'Apply'}</SectionHeading>
                <SectionSubheading>
                    {status === 'rejected'
                        ? 'You can apply again. Tell us what has changed.'
                        : draft.kind === 'manager'
                        ? 'Tell us about your roster. A person reads the answers.'
                        : 'Two questions. A person reads the answers.'}
                </SectionSubheading>
            </div>

            {draft.kind === 'creator' && (applications?.recruiting_manager || application?.manager) && (
                <Panel size={12}>
                    <p className='flex flex-row items-start gap-3 p-4 text-sm font-medium leading-relaxed text-white/70'>
                        <FontAwesomeIcon icon={faUserGroup} className='mt-1 shrink-0 text-[#03CEFF]' />
                        <span>
                            You are applying through{' '}
                            <span className='font-bold text-white'>
                                {application?.manager ?? applications?.recruiting_manager}
                            </span>
                            . They will see how your link performs, and you keep your full share —
                            their cut comes from BR1, not from you.
                        </span>
                    </p>
                </Panel>
            )}

            {status === 'rejected' && (
                <Panel size={12} className='border-white/10'>
                    <p className='flex flex-row items-start gap-3 p-4 text-sm font-medium leading-relaxed text-white/60'>
                        <FontAwesomeIcon icon={faRotateLeft} className='mt-1 shrink-0 text-white/40' />
                        {/*
                          The staff review note is never sent to the client, so there is nothing
                          specific to show here. Saying so plainly beats inventing a reason.
                        */}
                        Your last application was not accepted. Re-applying replaces it and puts you back in the
                        queue.
                    </p>
                </Panel>
            )}

            <Panel>
                <form onSubmit={onSubmit} className='flex flex-col gap-6 p-5 md:p-7'>
                    <Field
                        label={draft.kind === 'manager' ? 'Who do you represent?' : 'Where is your audience?'}
                        hint={
                            draft.kind === 'manager'
                                ? 'Your agency, and the creators on your roster — links are best.'
                                : 'Channels, handles, communities — links are best.'
                        }
                        htmlFor='audience'
                    >
                        <textarea
                            id='audience'
                            rows={4}
                            required
                            maxLength={4000}
                            value={draft.audience}
                            onChange={(e) => setDraft({ ...draft, audience: e.target.value })}
                            className={inputClass}
                            placeholder='twitch.tv/yourname, youtube.com/@yourname, a Discord you run...'
                        />
                    </Field>

                    <Field label='Roughly how big is it?' hint='Optional. A rough number is fine.' htmlFor='size'>
                        <input
                            id='size'
                            type='text'
                            maxLength={255}
                            value={draft.audience_size}
                            onChange={(e) => setDraft({ ...draft, audience_size: e.target.value })}
                            className={inputClass}
                            placeholder='e.g. 12k on Twitch, 800 in Discord'
                        />
                    </Field>

                    <Field
                        label={
                            draft.kind === 'manager'
                                ? 'How would you work with BR1?'
                                : 'What would you do with BR1?'
                        }
                        hint={
                            draft.kind === 'manager'
                                ? 'How you would bring your creators on and support them.'
                                : 'What you would make, and who it would reach.'
                        }
                        htmlFor='pitch'
                    >
                        <textarea
                            id='pitch'
                            rows={5}
                            required
                            maxLength={4000}
                            value={draft.pitch}
                            onChange={(e) => setDraft({ ...draft, pitch: e.target.value })}
                            className={inputClass}
                            placeholder='Weekly streams, a squad series with my community, short clips...'
                        />
                    </Field>

                    {submit.isError && (
                        <p className='text-sm font-medium text-red-400'>{(submit.error as Error).message}</p>
                    )}

                    <div className='flex flex-row items-center justify-end gap-3'>
                        {reapplying && (
                            <OutlineButton type='button' onClick={() => setReapplying(false)}>
                                Cancel
                            </OutlineButton>
                        )}
                        <button
                            type='submit'
                            disabled={submit.isLoading}
                            className={twMerge(
                                'button-gradient min-h-[2.75rem] rounded-none px-8 text-sm font-bold uppercase',
                                'tracking-wide transition-transform hover:scale-[1.02]',
                                'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100'
                            )}
                            style={submit.isSuccess ? { color: PRIZE_GREEN } : undefined}
                        >
                            {submit.isLoading ? 'Sending...' : 'Send application'}
                        </button>
                    </div>
                </form>
            </Panel>
        </>
    )
}
